import { Link, useBlocker } from '@tanstack/react-router';
import { useState, useRef } from 'react';
import { useDefinizioneFiltri } from '../api/anagrafiche.js';
import { ErroreApi } from '../api/cliente.js';
import {
  type Contatto,
  type Indirizzo,
  type Recapito,
  type TipoRecapito,
  useEliminaRecapito,
  useEliminaContatto,
  useEliminaIndirizzo,
  useLimiti,
  useOpzioni,
  useSalvaCategorie,
  useSalvaTag,
  useSalvaPreferenze,
  useSalvaAnagrafica,
  useScheda,
} from '../api/scheda.js';
import { CampoBc } from '../componenti/CampoBc.js';
import { Tabella } from '../componenti/Tabella.js';
import { etichettaTipoRecapito } from '../etichette.js';
import { ModaleContatto } from './ModaleContatto.js';
import { ModaleConverti } from './ModaleConverti.js';
import { OrdiniDelCliente } from './OrdiniDelCliente.js';
import { usePuoAprire } from '../permessi.js';
import { ModaleIndirizzo } from './ModaleIndirizzo.js';
import { ModaleRecapito } from './ModaleRecapito.js';
import { SchedaAccesso } from './SchedaAccesso.js';
import { t } from '../i18n/lingua.js';

/**
 * La scheda di un'anagrafica: dati, indirizzi, contatti, recapiti.
 *
 * Il salvataggio è esplicito, come nel v2 — c'è un pulsante Salva e finché
 * non lo premi non è successo niente — perché su una scheda con dieci campi
 * il salvataggio automatico rende impossibile ripensarci.
 */

const SCHEDE = [
  { chiave: 'dati', titolo: t('Dati principali') },
  { chiave: 'indirizzi', titolo: t('Indirizzi') },
  { chiave: 'contatti', titolo: t('Contatti') },
  { chiave: 'recapiti', titolo: t('Recapiti') },
  { chiave: 'ordini', titolo: t('Ordini') },
] as const;

type Scheda = (typeof SCHEDE)[number]['chiave'];

export function Anagrafica({ id, elenco }: { id: number; elenco: string }) {
  const puo = usePuoAprire();
  const [scheda, setScheda] = useState<Scheda>('dati');
  const [modifiche, setModifiche] = useState<Record<string, string>>({});
  /**
   * Uscendo dalla scheda con modifiche non salvate si chiede: prima si
   * perdevano senza dirlo. Il valore arriva più sotto, quando la scheda è
   * caricata; il blocco sta qui perché un hook non può stare dopo un return.
   */
  const sporca = useRef(false);
  useBlocker({
    shouldBlockFn: () =>
      sporca.current && !window.confirm(t('Ci sono modifiche non salvate: le perdo e vado avanti?')),
    enableBeforeUnload: () => sporca.current,
  });
  const [indirizzoAperto, setIndirizzoAperto] = useState<Indirizzo | null | undefined>(undefined);
  const [contattoAperto, setContattoAperto] = useState<Contatto | null | undefined>(undefined);
  const [preferenze, setPreferenze] = useState<Record<string, unknown>>({});
  const [categorie, setCategorie] = useState<number[] | null>(null);
  const [tag, setTag] = useState<number[] | null>(null);
  const [conversioneAperta, setConversioneAperta] = useState(false);
  const [recapitoAperto, setRecapitoAperto] = useState<
    { tipo: TipoRecapito; recapito: Recapito | null; contactId?: number } | undefined
  >(undefined);

  const dati = useScheda(id);
  const limiti = useLimiti();
  const definizioni = useDefinizioneFiltri();
  const opzioni = useOpzioni();
  const salvaPreferenze = useSalvaPreferenze(id);
  const salvaCategorie = useSalvaCategorie(id);
  const salvaTag = useSalvaTag(id);
  const salva = useSalvaAnagrafica(id);
  const eliminaIndirizzo = useEliminaIndirizzo(id);
  const eliminaContatto = useEliminaContatto(id);
  const eliminaEmail = useEliminaRecapito(id, 'emails');
  const eliminaTelefono = useEliminaRecapito(id, 'phones');
  const eliminaSito = useEliminaRecapito(id, 'websites');

  // con un numero non valido la lettura non parte nemmeno: «in attesa» per
  // sempre. Si aspetta solo una lettura vera, e il resto è «non disponibile»
  if (dati.isLoading) return <div className="cantiere">{t('Un momento…')}</div>;

  if (dati.error || !dati.data) {
    const messaggio =
      dati.error instanceof ErroreApi ? dati.error.message : t('Non è stato possibile aprire la scheda.');
    return (
      <>
        <div className="page-head">
          <div>
            <Link className="back-link" to={elenco as never}>
              {t('← Torna all\'elenco')}
            </Link>
            <h1 className="page-title">{t('Anagrafica non disponibile')}</h1>
          </div>
        </div>
        <div className="cantiere">
          <p>{messaggio}</p>
        </div>
      </>
    );
  }

  const a = dati.data!;
  const limitiBc = limiti.data?.limits ?? {};
  // tutti i paesi, non solo quelli già usati: un indirizzo nuovo può essere altrove
  const paesi = definizioni.data?.options.allCountries ?? definizioni.data?.options.countries ?? [];
  const valore = (campo: keyof typeof a) =>
    modifiche[campo] ?? ((a[campo] as string | null) ?? '');
  const cambia = (campo: string) => (v: string) => setModifiche((m) => ({ ...m, [campo]: v }));

  /** Le preferenze: quello che si sta modificando, o quello che c'è a database. */
  const preferenza = (campo: string): unknown =>
    campo in preferenze
      ? preferenze[campo]
      : ((a.preferences as Record<string, unknown> | null)?.[campo] ?? null);
  const cambiaPreferenza = (campo: string, valore: unknown) =>
    setPreferenze((p) => ({ ...p, [campo]: valore }));

  const categorieScelte = categorie ?? a.categories.map((c) => c.id);
  const categorieCambiate =
    categorie !== null &&
    (categorie.length !== a.categories.length ||
      categorie.some((id) => !a.categories.some((c) => c.id === id)));

  const tagScelti = tag ?? a.tags.map((t) => t.id);
  const tagCambiati =
    tag !== null &&
    (tag.length !== a.tags.length || tag.some((id) => !a.tags.some((t) => t.id === id)));

  const daSalvare =
    Object.keys(modifiche).length > 0 ||
    Object.keys(preferenze).length > 0 ||
    categorieCambiate ||
    tagCambiati;
  sporca.current = daSalvare;
  const inCorso =
    salva.isPending || salvaPreferenze.isPending || salvaCategorie.isPending || salvaTag.isPending;

  /**
   * Un pulsante solo salva tutto quello che è cambiato nella scheda: campi,
   * preferenze, categorie e tag. Sono quattro chiamate diverse perché sono
   * quattro tabelle diverse, ma chi lavora vede una modifica sola.
   */
  const salvaTutto = () => {
    if (Object.keys(modifiche).length > 0) {
      const dati: Record<string, unknown> = {};
      for (const [chiave, v] of Object.entries(modifiche)) dati[chiave] = v.trim() || null;
      if (typeof dati.name === 'string' || dati.name === null)
        dati.name = (modifiche.name ?? '').trim();
      salva.mutate(dati, { onSuccess: () => setModifiche({}) });
    }
    if (Object.keys(preferenze).length > 0) {
      salvaPreferenze.mutate(preferenze, { onSuccess: () => setPreferenze({}) });
    }
    if (categorieCambiate && categorie !== null) {
      salvaCategorie.mutate(categorie, { onSuccess: () => setCategorie(null) });
    }
    if (tagCambiati && tag !== null) {
      salvaTag.mutate(tag, { onSuccess: () => setTag(null) });
    }
  };

  const erroreSalvataggio =
    (salva.error instanceof ErroreApi && salva.error) ||
    (salvaPreferenze.error instanceof ErroreApi && salvaPreferenze.error) ||
    (salvaCategorie.error instanceof ErroreApi && salvaCategorie.error) ||
    (salvaTag.error instanceof ErroreApi && salvaTag.error) ||
    null;
  const esitoEliminazione = eliminaIndirizzo.data;

  return (
    <>
      <div className="page-head">
        <div>
          <Link className="back-link" to={elenco as never}>
            {t('← Torna all\'elenco')}
          </Link>
          <h1 className="page-title">{a.name}</h1>
          <div className="page-sub">
            {a.bcCodeSpa && (
              <>
                <span className="num">{a.bcCodeSpa}</span> {t('in BC SPA')}{' '}
              </>
            )}
            {a.bcCodeInc && (
              <>
                · <span className="num">{a.bcCodeInc}</span> {t('in BC Inc')}{' '}
              </>
            )}
            {a.companies.length > 0 &&
              `· ${t('gestita in {aziende}', { aziende: a.companies.map((c) => c.code).join(` ${t('e')} `) })}`}
            {a.archived && ` · ${t('archiviata')}`}
          </div>
        </div>
        <div className="page-actions">
          {daSalvare && <span className="badge badge-warn">{t('modifiche non salvate')}</span>}
          {/* Un operatore non si converte: ha le credenziali, e il pulsante
              non deve nemmeno comparire per poi dare un errore. */}
          {a.type !== 'operator' && (
            <button className="btn" onClick={() => setConversioneAperta(true)}>
              {t('Converti')}
            </button>
          )}
          <button
            className="btn btn-primary"
            onClick={salvaTutto}
            disabled={!daSalvare || inCorso}
          >
            {inCorso ? 'Salvo…' : 'Salva'}
          </button>
        </div>
      </div>

      {conversioneAperta && (
        <ModaleConverti
          id={id}
          nome={a.name}
          tipo={a.type}
          onChiudi={() => setConversioneAperta(false)}
        />
      )}

      <div className="tabs">
        {SCHEDE.filter((s) => s.chiave !== 'ordini' || puo('sales-orders')).map((s) => (
          <button
            key={s.chiave}
            className="tab"
            aria-selected={scheda === s.chiave}
            onClick={() => setScheda(s.chiave)}
          >
            {s.titolo}
            {s.chiave === 'indirizzi' && <span className="count">{a.addresses.length}</span>}
            {s.chiave === 'contatti' && <span className="count">{a.contacts.length}</span>}
            {s.chiave === 'recapiti' && (
              // solo quelli dell'azienda, come la linguetta: quelli delle persone
              // stanno in «Contatti», e contarli qui faceva un numero che non tornava
              <span className="count">
                {a.emails.filter((e) => e.contactId === null).length +
                  a.phones.filter((p) => p.contactId === null).length +
                  a.websites.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {erroreSalvataggio && (
        <div className="avviso" role="alert" style={{ margin: '12px 20px 0' }}>
          <span className="avviso-segno">▲</span>
          <span>{erroreSalvataggio.message}</span>
        </div>
      )}

      {scheda === 'dati' && (
        <div className="grid grid-2">
          <div className="card">
            <div className="card-head">
              <span className="card-title">{t('Anagrafica')}</span>
            </div>
            <div className="card-body">
              <div className="fields">
                <CampoBc
                  id="a-nome"
                  etichetta="Ragione sociale"
                  obbligatorio
                  larghezzaPiena
                  valore={valore('name')}
                  limite={limitiBc.name}
                  onCambia={cambia('name')}
                />
                <CampoBc
                  id="a-bcspa"
                  etichetta="Codice in BC — SPA"
                  monospazio
                  valore={valore('bcCodeSpa')}
                  limite={limitiBc.bcCode}
                  onCambia={cambia('bcCodeSpa')}
                />
                <CampoBc
                  id="a-bcinc"
                  etichetta="Codice in BC — Inc"
                  monospazio
                  valore={valore('bcCodeInc')}
                  limite={limitiBc.bcCode}
                  onCambia={cambia('bcCodeInc')}
                />
                <CampoBc
                  id="a-piva"
                  etichetta="Partita IVA"
                  monospazio
                  valore={valore('taxNumber')}
                  limite={limitiBc.taxNumber}
                  onCambia={cambia('taxNumber')}
                />
                <CampoBc
                  id="a-cf"
                  etichetta="Codice fiscale"
                  monospazio
                  valore={valore('taxCardId')}
                  limite={limitiBc.taxCardId}
                  onCambia={cambia('taxCardId')}
                />
                <CampoBc
                  id="a-agente"
                  etichetta="Informazioni agente"
                  larghezzaPiena
                  valore={valore('agentInfo')}
                  onCambia={cambia('agentInfo')}
                />
                <div className="field span-2">
                  <label htmlFor="a-note">{t('Annotazioni')}</label>
                  <textarea
                    className="textarea"
                    id="a-note"
                    value={valore('notes')}
                    onChange={(e) => cambia('notes')(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="stack">
            <div className="card">
              <div className="card-head">
                <span className="card-title">{t('Classificazione')}</span>
              </div>
              <div className="card-body">
                {/* Le categorie si spuntano da un elenco: sono poche e
                    stabili, e un menu a tendina nasconderebbe quali sono
                    già assegnate. */}
                <div className="row wrap" style={{ gap: 6 }}>
                  {opzioni.data?.categories.length === 0 && (
                    <span className="dim">
                      {t('Nessuna categoria definita per questa azienda: si creano dai Dati generici.')}
                    </span>
                  )}
                  {opzioni.data?.categories.map((c) => {
                    const scelta = categorieScelte.includes(c.id);
                    return (
                      <button
                        key={c.id}
                        className={scelta ? 'chip chip-set' : 'chip'}
                        onClick={() =>
                          setCategorie(
                            scelta
                              ? categorieScelte.filter((x) => x !== c.id)
                              : [...categorieScelte, c.id],
                          )
                        }
                      >
                        {scelta ? '✓ ' : '+ '}
                        {c.label}
                      </button>
                    );
                  })}
                </div>

                {/* I tag sono un'altra cosa dalle categorie: la categoria dice
                    che tipo di soggetto è, il tag da dove è arrivato. Stessa
                    forma perché si usano allo stesso modo, separati da una
                    riga perché rispondono a due domande diverse. */}
                <div className="field" style={{ marginTop: 14 }}>
                  <label>{t('Da dove arriva')}</label>
                  <div className="row wrap" style={{ gap: 6 }}>
                    {opzioni.data?.tags.length === 0 && (
                      <span className="dim">
                        {t('Nessun tag definito per questa azienda: si creano dai Dati generici.')}
                      </span>
                    )}
                    {opzioni.data?.tags.map((tg) => {
                      const scelto = tagScelti.includes(tg.id);
                      return (
                        <button
                          key={tg.id}
                          className={scelto ? 'chip chip-set' : 'chip'}
                          onClick={() =>
                            setTag(
                              scelto ? tagScelti.filter((x) => x !== tg.id) : [...tagScelti, tg.id],
                            )
                          }
                        >
                          {scelto ? '✓ ' : '+ '}
                          {tg.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* L'accesso al programma: solo per gli operatori, e solo per chi
                ha il livello per darlo. Nel v2 sta esattamente qui, sotto i
                dati anagrafici; nella 3.0 mancava, e creare un operatore non
                gli dava l'accesso. */}
            {a.type === 'operator' && <SchedaAccesso id={a.id} nome={a.name} />}

            {a.preferences && (
              <div className="card">
                <div className="card-head">
                  <span className="card-title">{t('Preferenze commerciali')}</span>
                </div>
                <div className="card-body">
                  <div className="fields">
                    <div className="field">
                      <label htmlFor="p-valuta">{t('Valuta')}</label>
                      <select
                        className="select"
                        id="p-valuta"
                        value={preferenza('defaultCurrency') as string}
                        onChange={(e) => cambiaPreferenza('defaultCurrency', e.target.value || null)}
                      >
                        <option value="">{t('Non impostata')}</option>
                        {opzioni.data?.currencies.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="field">
                      <label htmlFor="p-sezionale">{t('Sezionale')}</label>
                      <select
                        className="select"
                        id="p-sezionale"
                        value={String(preferenza('defaultSectionalId') ?? '')}
                        onChange={(e) =>
                          cambiaPreferenza(
                            'defaultSectionalId',
                            e.target.value ? Number(e.target.value) : null,
                          )
                        }
                      >
                        <option value="">{t('Non impostato')}</option>
                        {opzioni.data?.sectionals.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="field span-2">
                      <label htmlFor="p-pagamento">{t('Tipo di pagamento')}</label>
                      <select
                        className="select"
                        id="p-pagamento"
                        value={String(preferenza('defaultPaymentTypeId') ?? '')}
                        onChange={(e) =>
                          cambiaPreferenza(
                            'defaultPaymentTypeId',
                            e.target.value ? Number(e.target.value) : null,
                          )
                        }
                      >
                        <option value="">{t('Non impostato')}</option>
                        {opzioni.data?.paymentTypes.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="field span-2">
                      <label htmlFor="p-banca">{t('Banca')}</label>
                      <select
                        className="select"
                        id="p-banca"
                        value={String(preferenza('defaultBankId') ?? '')}
                        onChange={(e) =>
                          cambiaPreferenza(
                            'defaultBankId',
                            e.target.value ? Number(e.target.value) : null,
                          )
                        }
                      >
                        <option value="">{t('Non impostata')}</option>
                        {opzioni.data?.banks.map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {(
                    [
                      ['prepayment', 'Pagamento anticipato'],
                      ['sendDataToCarriers', 'Invia i dati di spedizione ai vettori'],
                      ['automaticSupply', 'Approvvigionamento automatico'],
                      ['directTransaction', 'Transazione diretta'],
                      ['requestCurrency', 'Chiede la valuta sull’ordine'],
                    ] as const
                  ).map(([chiave, testo]) => (
                    <div className="switch-row" key={chiave}>
                      <button
                        className="switch"
                        role="switch"
                        aria-checked={Boolean(preferenza(chiave))}
                        onClick={() => cambiaPreferenza(chiave, !preferenza(chiave))}
                      />
                      <span>{testo}</span>
                    </div>
                  ))}
                </div>
                <div className="section-note">
                  {t('Sono i valori che l\'ordine si porta dietro quando lo si crea.')}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {scheda === 'indirizzi' && (
        <>
          <div className="page-head" style={{ paddingBottom: 8 }}>
            <div>
              <h2 className="page-title" style={{ fontSize: 15 }}>
                {t('Indirizzi e destinazioni')}
              </h2>
              <div className="page-sub">
                {t('Le destinazioni finiscono negli ordini e viaggiano verso Business Central')}
              </div>
            </div>
            <div className="page-actions">
              <button className="btn btn-primary" onClick={() => setIndirizzoAperto(null)}>
                {t('Nuovo indirizzo')}
              </button>
            </div>
          </div>

          {esitoEliminazione?.archived && (
            <div className="avviso" style={{ margin: '0 20px 12px', background: 'var(--warn-soft)', color: 'var(--warn)', borderColor: 'transparent' }}>
              <span className="avviso-segno">▲</span>
              <span>{esitoEliminazione.message}</span>
            </div>
          )}

          <div className="table-wrap" style={{ marginBottom: 24 }}>
            <Tabella
              colonne={[
                {
                  chiave: 'name',
                  titolo: t('Denominazione'),
                  cella: (r: Indirizzo) => (
                    <>
                      <a
                        className="cell-link"
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setIndirizzoAperto(r);
                        }}
                      >
                        {r.name}
                      </a>
                      {r.isMain && (
                        <span className="badge badge-accent" style={{ marginLeft: 8 }}>
                          {t('principale')}
                        </span>
                      )}
                      {r.archived && (
                        <span className="badge" style={{ marginLeft: 8 }}>
                          {t('archiviato')}
                        </span>
                      )}
                    </>
                  ),
                },
                { chiave: 'address', titolo: t('Indirizzo') },
                { chiave: 'city', titolo: t('Città') },
                { chiave: 'provinceState', titolo: t('Prov./Stato'), monospazio: true },
                { chiave: 'postalCode', titolo: t('CAP/ZIP'), monospazio: true },
                { chiave: 'country', titolo: t('Paese') },
                { chiave: 'destinationCode', titolo: t('Cod. destinazione'), monospazio: true },
                {
                  chiave: 'bc',
                  titolo: 'BC',
                  // in quale Business Central c'è: un ordine verso una
                  // destinazione che la sua BC non ha, in BC non la trova.
                  // L'indirizzo principale in BC è quello del cliente stesso
                  cella: (r: Indirizzo) =>
                    r.bc.length > 0 ? (
                      r.bc.map((codice) => (
                        <span key={codice} className="badge" style={{ marginRight: 4 }}>
                          {codice === 'INC' ? 'Inc' : codice}
                        </span>
                      ))
                    ) : r.isMain ? (
                      <span className="dim">{t('del cliente')}</span>
                    ) : (
                      <span className="dim">—</span>
                    ),
                },
                {
                  chiave: 'azioni',
                  titolo: '',
                  cella: (r: Indirizzo) => (
                    <button
                      className="btn btn-ghost"
                      style={{ height: 22 }}
                      onClick={() => eliminaIndirizzo.mutate(r.id)}
                      disabled={eliminaIndirizzo.isPending}
                    >
                      {t('Elimina')}
                    </button>
                  ),
                },
              ]}
              righe={a.addresses}
              chiaveRiga={(r) => r.id}
              onApri={(r) => setIndirizzoAperto(r)}
              vuoto={t('Nessun indirizzo. Aggiungine uno per poter spedire.')}
            />
          </div>
          {a.addressesNotInBc > 0 && (
            <p className="dim" style={{ margin: '-12px 20px 24px', fontSize: 12 }}>
              {a.addressesNotInBc === 1
                ? t('Una destinazione di questo cliente non è nella Business Central della Inc, e qui non si vede.')
                : t('{n} destinazioni di questo cliente non sono nella Business Central della Inc, e qui non si vedono.', {
                    n: a.addressesNotInBc,
                  })}
            </p>
          )}
        </>
      )}

      {scheda === 'contatti' && (
        <>
          <div className="page-head" style={{ paddingBottom: 8 }}>
            <div>
              <h2 className="page-title" style={{ fontSize: 15 }}>
                {t('Contatti')}
              </h2>
              <div className="page-sub">{t('Le persone con cui si parla in questa azienda')}</div>
            </div>
            <div className="page-actions">
              <button className="btn btn-primary" onClick={() => setContattoAperto(null)}>
                {t('Nuovo contatto')}
              </button>
            </div>
          </div>
          {/* Una persona con i suoi recapiti sotto, non una riga di tabella:
              19 contatti su 22 hanno almeno un telefono o un'e-mail, e
              saperli attaccati alla persona giusta è il motivo per cui
              esistono i contatti invece dei soli recapiti aziendali. */}
          <div className="grid" style={{ paddingTop: 4 }}>
            {a.contacts.length === 0 && (
              <div className="cantiere">
                <p>{t('Nessun contatto. Aggiungine uno per tenere i recapiti delle persone.')}</p>
              </div>
            )}

            {a.contacts.map((c) => {
              const sue = {
                emails: a.emails.filter((e) => e.contactId === c.id),
                phones: a.phones.filter((p) => p.contactId === c.id),
              };
              return (
                <div className="card" key={c.id}>
                  <div className="card-head">
                    <span className="strong">
                      {c.firstName} {c.lastName}
                    </span>
                    {c.role && <span className="dim">· {c.role}</span>}
                    {c.officeName && <span className="dim">· {c.officeName}</span>}
                    <button className="btn" onClick={() => setContattoAperto(c)}>
                      {t('Modifica')}
                    </button>
                    <button
                      className="btn btn-ghost"
                      onClick={() => eliminaContatto.mutate(c.id)}
                      disabled={eliminaContatto.isPending}
                    >
                      {t('Elimina')}
                    </button>
                  </div>
                  <div className="card-body">
                    <div className="stack">
                      {sue.emails.length === 0 && sue.phones.length === 0 && (
                        <span className="dim">{t('Nessun recapito per questa persona.')}</span>
                      )}

                      {[
                        { tipo: 'emails' as const, righe: sue.emails, campo: 'email' as const, elimina: eliminaEmail },
                        { tipo: 'phones' as const, righe: sue.phones, campo: 'phoneNumber' as const, elimina: eliminaTelefono },
                      ].map((g) =>
                        g.righe.map((r) => (
                          <div className="row" key={`${g.tipo}-${r.id}`}>
                            <span className="num">{r[g.campo]}</span>
                            {r.kind && <span className="badge">{etichettaTipoRecapito(r.kind)}</span>}
                            {r.isMain && <span className="badge badge-accent">{t('predefinito')}</span>}
                            {g.tipo === 'emails' && r.notifyEtaDeviation && (
                              <span className="badge badge-accent">{t('ritardi ETA')}</span>
                            )}
                            <span style={{ marginLeft: 'auto' }} />
                            <button
                              className="btn btn-ghost"
                              style={{ height: 22 }}
                              onClick={() =>
                                setRecapitoAperto({ tipo: g.tipo, recapito: r, contactId: c.id })
                              }
                            >
                              {t('Modifica')}
                            </button>
                            <button
                              className="btn btn-ghost"
                              style={{ height: 22 }}
                              onClick={() => g.elimina.mutate(r.id)}
                            >
                              {t('Elimina')}
                            </button>
                          </div>
                        )),
                      )}
                    </div>
                  </div>
                  <div className="section-note">
                    <span className="row" style={{ gap: 6 }}>
                      <button
                        className="btn"
                        style={{ height: 24 }}
                        onClick={() =>
                          setRecapitoAperto({ tipo: 'emails', recapito: null, contactId: c.id })
                        }
                      >
                        {t('+ E-mail')}
                      </button>
                      <button
                        className="btn"
                        style={{ height: 24 }}
                        onClick={() =>
                          setRecapitoAperto({ tipo: 'phones', recapito: null, contactId: c.id })
                        }
                      >
                        {t('+ Telefono')}
                      </button>
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {scheda === 'recapiti' && (
        <div className="grid">
          {(
            [
              {
                tipo: 'emails' as const,
                titolo: t('Indirizzi e-mail'),
                righe: a.emails.filter((e) => e.contactId === null),
                elimina: eliminaEmail,
                vuoto: t('Nessun indirizzo e-mail.'),
                colonne: [
                  { chiave: 'email', titolo: t('Indirizzo'), monospazio: true },
                  {
                    chiave: 'kind',
                    titolo: t('Tipo'),
                    cella: (r: Recapito) =>
                      r.kind ? etichettaTipoRecapito(r.kind) : <span className="dim">—</span>,
                  },
                  {
                    chiave: 'avvisi',
                    titolo: t('Riceve gli avvisi'),
                    cella: (r: Recapito) => (
                      <span className="row wrap" style={{ gap: 4 }}>
                        {r.notifyEtaDeviation && <span className="badge badge-accent">{t('ritardi ETA')}</span>}
                        {r.notifyStateDowngrade && (
                          <span className="badge badge-accent">{t('stato indietro')}</span>
                        )}
                        {r.notifyPricelistApproval && <span className="badge badge-accent">{t('listini')}</span>}
                        {!r.notifyEtaDeviation &&
                          !r.notifyStateDowngrade &&
                          !r.notifyPricelistApproval && <span className="dim">{t('nessuno')}</span>}
                      </span>
                    ),
                  },
                ],
              },
              {
                tipo: 'phones' as const,
                titolo: t('Telefoni'),
                righe: a.phones.filter((p) => p.contactId === null),
                elimina: eliminaTelefono,
                vuoto: t('Nessun numero di telefono.'),
                colonne: [
                  { chiave: 'phoneNumber', titolo: t('Numero'), monospazio: true },
                  {
                    chiave: 'kind',
                    titolo: t('Tipo'),
                    cella: (r: Recapito) =>
                      r.kind ? etichettaTipoRecapito(r.kind) : <span className="dim">—</span>,
                  },
                ],
              },
              {
                tipo: 'websites' as const,
                titolo: t('Siti web'),
                righe: a.websites,
                elimina: eliminaSito,
                vuoto: t('Nessun sito web.'),
                colonne: [
                  { chiave: 'url', titolo: t('Indirizzo') },
                  {
                    chiave: 'kind',
                    titolo: t('Tipo'),
                    cella: (r: Recapito) =>
                      r.kind ? etichettaTipoRecapito(r.kind) : <span className="dim">—</span>,
                  },
                ],
              },
            ] as const
          ).map((gruppo) => (
            <div className="card" key={gruppo.tipo}>
              <div className="card-head">
                <span className="card-title">{gruppo.titolo}</span>
                <button
                  className="btn"
                  onClick={() => setRecapitoAperto({ tipo: gruppo.tipo, recapito: null })}
                >
                  {t('Aggiungi')}
                </button>
              </div>
              <div className="card-body tight">
                <Tabella
                  colonne={[
                    ...gruppo.colonne,
                    {
                      chiave: 'principale',
                      titolo: '',
                      cella: (r: Recapito) =>
                        r.isMain ? <span className="badge badge-accent">{t('predefinito')}</span> : null,
                    },
                    {
                      chiave: 'azioni',
                      titolo: '',
                      allineamento: 'destra' as const,
                      cella: (r: Recapito) => (
                        <span className="row" style={{ gap: 4, justifyContent: 'flex-end' }}>
                          <button
                            className="btn btn-ghost"
                            style={{ height: 22 }}
                            onClick={() => setRecapitoAperto({ tipo: gruppo.tipo, recapito: r })}
                          >
                            {t('Modifica')}
                          </button>
                          <button
                            className="btn btn-ghost"
                            style={{ height: 22 }}
                            onClick={() => gruppo.elimina.mutate(r.id)}
                          >
                            {t('Elimina')}
                          </button>
                        </span>
                      ),
                    },
                  ]}
                  righe={gruppo.righe}
                  chiaveRiga={(r) => r.id}
                  onApri={(r) => setRecapitoAperto({ tipo: gruppo.tipo, recapito: r })}
                  vuoto={gruppo.vuoto}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* la domanda più frequente su un cliente è «cosa ha in giro»: nel v2
          bisogna uscire dalla scheda e rifare la ricerca dall'elenco ordini */}
      {scheda === 'ordini' && puo('sales-orders') && <OrdiniDelCliente identityId={id} />}

      {indirizzoAperto !== undefined && (
        <ModaleIndirizzo
          identityId={id}
          indirizzo={indirizzoAperto}
          paesi={paesi}
          limiti={limitiBc}
          onChiudi={() => setIndirizzoAperto(undefined)}
        />
      )}

      {contattoAperto !== undefined && (
        <ModaleContatto
          identityId={id}
          contatto={contattoAperto}
          limiti={limitiBc}
          onChiudi={() => setContattoAperto(undefined)}
        />
      )}

      {recapitoAperto !== undefined && (
        <ModaleRecapito
          identityId={id}
          tipo={recapitoAperto.tipo}
          recapito={recapitoAperto.recapito}
          contactId={recapitoAperto.contactId ?? null}
          limiti={limitiBc}
          onChiudi={() => setRecapitoAperto(undefined)}
        />
      )}
    </>
  );
}
